import React from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { User } from "model/user";
import * as yup from "yup";
import { validate } from "shared/yup";
import * as Icons from "react-feather";
import * as urls from "api/urls";
import * as credentials from "shared/credentials";
import { styles } from "./style";
import clsx from "clsx";
import * as env from "shared/env";

interface OwnProps {
	open: boolean;
	onClose: () => void;
	user: User;
}

interface Props extends
	OwnProps,
	WithStyles<typeof styles> {}

interface Category {
	id: number;
	name: string;
	description?: string;
	position?: number;
}

interface State {
	title: string;
	description: string;
	categoryId: number | null;
	categories: Category[];
	attachments: File[];
	submitted: boolean;
	submitting: boolean;
	dragActive: boolean;
	showSuccess: boolean;
	categoriesLoaded: boolean;
}

class Component extends React.Component<Props, State> {
	private fileInputRef = React.createRef<HTMLInputElement>();

	public constructor(props: Props) {
		super(props);
		this.state = {
			title: "",
			description: "",
			categoryId: null,
			categories: [],
			attachments: [],
			submitted: false,
			submitting: false,
			dragActive: false,
			showSuccess: false,
			categoriesLoaded: false,
		};
	}

	componentDidMount() {
		this.loadCategories();
	}

	private loadCategories = async () => {
		try {
			const auth = credentials.get();
			if (!auth) {
				this.setState({ categoriesLoaded: true, categories: [] });
				return;
			}
			const encoded = btoa(`${auth.username}:${auth.password}`);

			const categoriesUrl = urls.feedbackCategories();

			const response = await fetch(categoriesUrl, {
				headers: {
					"Authorization": `Basic ${encoded}`,
					"X-Source": "controlPanel",
				},
			});

			if (response.ok) {
				const data = await response.json();
				this.setState({
					categories: data.categories || [],
					categoriesLoaded: true,
				});
			} else {
				this.setState({ categoriesLoaded: true, categories: [] });
			}
		} catch(error) {
			this.setState({ categoriesLoaded: true, categories: [] });
		}
	}

	private validationSchema = {
		title: yup.string().required("Title is required"),
		description: yup.string().required("Description is required"),
	};

	private handleDrag = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		if (e.type === "dragenter" || e.type === "dragover") {
			this.setState({ dragActive: true });
		} else if (e.type === "dragleave") {
			this.setState({ dragActive: false });
		}
	};

	private handleDrop = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		this.setState({ dragActive: false });

		if (e.dataTransfer.files && e.dataTransfer.files[0]) {
			this.handleFiles(e.dataTransfer.files);
		}
	};

	private handleFiles = (files: FileList) => {
		const fileArray = Array.from(files);
		const maxSize = 25 * 1024 * 1024; // 25 MB in bytes

		const validFiles = fileArray.filter(file => {
			if (file.size > maxSize) {
				alert(`File "${file.name}" exceeds the maximum size of 25 MB`);
				return false;
			}
			return true;
		});

		this.setState(prevState => ({
			attachments: [...prevState.attachments, ...validFiles]
		}));
	};

	private handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.files) {
			this.handleFiles(e.target.files);
		}
	};

	private removeAttachment = (index: number) => {
		this.setState(prevState => {
			const file = prevState.attachments[index];
			if (file) {
				URL.revokeObjectURL(URL.createObjectURL(file));
			}
			return {
				attachments: prevState.attachments.filter((_, i) => i !== index)
			};
		});
	};

	componentWillUnmount() {
		this.state.attachments.forEach(file => {
			URL.revokeObjectURL(URL.createObjectURL(file));
		});
	}

	private handleSubmit = async () => {
		const { title, description, categoryId, attachments } = this.state;

		const errors = validate(this.validationSchema, this.state);

		if (errors) {
			this.setState({ submitted: true });
			return;
		}

		this.setState({ submitting: true });

		try {
			const auth = credentials.get();
			if (!auth) {
				throw new Error("Authentication required. Please log in again.");
			}
			const encoded = btoa(`${auth.username}:${auth.password}`);

			const formData = new FormData();
			if (title) {
				formData.append('title', title);
			}
			formData.append('text', description);
			if (categoryId !== null) {
				formData.append('categoryId', categoryId.toString());
			}

			if (attachments && attachments.length > 0) {
				attachments.forEach((file) => {
					formData.append('files', file);
				});
			}

			const feedbackUrl = urls.feedback();

			const response = await fetch(feedbackUrl, {
				method: 'POST',
				headers: {
					"Authorization": `Basic ${encoded}`,
					"X-Source": "controlPanel",
				},
				body: formData,
			});

			let result;
			const contentType = response.headers.get('content-type');

			try {
				const responseText = await response.text();

				if (responseText && contentType?.includes('application/json')) {
					result = JSON.parse(responseText);
				} else {
					throw new Error(`Server returned ${response.status}: ${contentType || 'unknown content type'}`);
				}
			} catch(parseError) {
				throw new Error(`Server error (${response.status}): Unable to parse response`);
			}

			if (response.ok) {
				if (result.success) {
					this.setState({
						submitting: false,
						showSuccess: true,
					});
				} else {
					throw new Error(result.errorMessage || "Failed to submit idea");
				}
			} else {
				const errorMessage = result.errorMessage || `Server error (${response.status})`;
				throw new Error(errorMessage);
			}
		} catch(error) {
			this.setState({ submitting: false });
			const errorMsg = error instanceof Error ? error.message : "Unknown error";
			alert(`There was an error submitting your idea: ${errorMsg}\n\nPlease try again.`);
		}
	};

	private handleClose = () => {
		if (!this.state.submitting) {
			this.setState({
				title: "",
				description: "",
				categoryId: null,
				attachments: [],
				submitted: false,
				showSuccess: false,
			});
			this.props.onClose();
		}
	};

	private handlePostAnother = () => {
		this.setState({
			title: "",
			description: "",
			categoryId: null,
			attachments: [],
			submitted: false,
			showSuccess: false,
		});
	};

	render() {
		const { open, classes = {} } = this.props;
		const { title, description, categoryId, categories, attachments, submitted, submitting, dragActive, showSuccess } = this.state;
		const errors = validate(this.validationSchema, this.state);

		if (!open) return null;

		return (
			<>
				<Mui.Box
					className={clsx(
						classes.dialogBox,
						showSuccess ? classes.dialogBoxSuccess : classes.dialogBoxForm
					)}
				>
				{showSuccess ? (
					<>
						<Mui.Box className={classes.successHeader}>
							<Mui.IconButton onClick={this.handleClose} size="small" className={classes.successCloseButton}>
								<Icons.X/>
							</Mui.IconButton>
						</Mui.Box>
						<Mui.Box className={classes.successContent}>
							<Mui.Typography variant="h4" className={classes.successTitle}>
								Thank you!
							</Mui.Typography>
							<Mui.Typography variant="body1" className={classes.successMessage}>
								Your idea has been posted to our{' '}
								<a
									href="https://1900addison.uservoice.com/forums/967325-control-panel"
									target="_blank"
									rel="noopener noreferrer"
									className={classes.successLink}
								>
									feedback forum
								</a>
							</Mui.Typography>
							<Mui.Typography variant="caption" className={classes.successCaption}>
								<a
									onClick={this.handlePostAnother}
									className={classes.successPostAnotherLink}
								>
									Post another idea
								</a>
							</Mui.Typography>
						</Mui.Box>
					</>
				) : (
					<>
						<Mui.Box className={classes.formHeader}>
							<Mui.Box className={classes.headerSpacer} />
							<Mui.Typography variant="h6" className={classes.headerTitle}>
								Post an Idea
							</Mui.Typography>
							<Mui.IconButton onClick={this.handleClose} disabled={submitting} size="small" className={classes.headerCloseButton}>
								<Icons.X/>
							</Mui.IconButton>
						</Mui.Box>

						<Mui.Box className={classes.formContent}>
							<Mui.Box mb={3}>
								<Mui.Box mb={2}>
									<Mui.Typography variant="body2" color="textSecondary">
										Describe your idea, how it would help you, and why it matters.
									</Mui.Typography>
								</Mui.Box>
								<Mui.Typography variant="body2" color="textSecondary">
									This form is for product feedback only. For support, please{" "}
									<Mui.Link href="https://www.ihomefinder.com/resources/support/#contact" color="primary" target="_blank" rel="noopener noreferrer">
										contact us
									</Mui.Link>
									.
								</Mui.Typography>
							</Mui.Box>

							<Mui.Box mb={3}>
								<Mui.Typography variant="subtitle2" gutterBottom>
									<strong>Title</strong>
									<span className={classes.requiredAsterisk}>*</span>
								</Mui.Typography>
								<Mui.TextField
									fullWidth
									placeholder="Enter title here"
									variant="outlined"
									size="small"
									value={title}
									onChange={(e) => this.setState({ title: e.target.value })}
									error={submitted && !!errors?.title}
									helperText={submitted && errors?.title}
									disabled={submitting}
								/>
							</Mui.Box>

							<Mui.Box mb={3}>
								<Mui.Typography variant="subtitle2" gutterBottom>
									<strong>Description</strong>
									<span className={classes.requiredAsterisk}>*</span>
								</Mui.Typography>
								<Mui.TextField
									fullWidth
									multiline
									rows={4}
									placeholder="Describe your idea"
									variant="outlined"
									value={description}
									onChange={(e) => this.setState({ description: e.target.value })}
									error={submitted && !!errors?.description}
									helperText={submitted && errors?.description}
									disabled={submitting}
								/>
							</Mui.Box>

							<Mui.Box mb={3}>
								<Mui.Typography variant="subtitle2" gutterBottom>
									<strong>Which part of the platform?</strong>
								</Mui.Typography>
								<Mui.TextField
									select
									fullWidth
									variant="outlined"
									size="small"
									value={categoryId !== null ? categoryId : ""}
									onChange={(e) => this.setState({ categoryId: e.target.value === "" ? null : parseInt(e.target.value) })}
									disabled={submitting}
									SelectProps={{
										displayEmpty: true,
									}}
								>
									<Mui.MenuItem value="">
										<span className={classes.selectPlaceholder}>Choose the category</span>
									</Mui.MenuItem>
									{categories.map((cat) => (
										<Mui.MenuItem key={cat.id} value={cat.id}>
											{cat.name}
										</Mui.MenuItem>
									))}
								</Mui.TextField>
							</Mui.Box>

							<Mui.Box mb={2}>
								<Mui.Typography variant="subtitle2" gutterBottom>
									<strong>Attachments</strong>
								</Mui.Typography>

								<input
									ref={this.fileInputRef}
									type="file"
									multiple
									accept="image/*"
									onChange={this.handleFileInputChange}
									style={{ display: 'none' }}
									disabled={submitting}
								/>

								{attachments.length === 0 ? (
									<Mui.Box
										onDragEnter={this.handleDrag}
										onDragOver={this.handleDrag}
										onDragLeave={this.handleDrag}
										onDrop={this.handleDrop}
										className={clsx(
											classes.uploadBox,
											dragActive && classes.uploadBoxActive,
											submitting && classes.uploadBoxDisabled
										)}
										onClick={() => !submitting && this.fileInputRef.current?.click()}
									>
										<Mui.Typography color="secondary">
											<Mui.Box className={classes.uploadIcon}>
												<Icons.Upload/>
											</Mui.Box>
										</Mui.Typography>
										<Mui.Typography variant="body2" color="secondary" className={classes.uploadText}>
											Click to Upload{" "}
											<span className={classes.uploadTextSecondary}>or drag and drop</span>
										</Mui.Typography>
										<Mui.Typography variant="caption" color="textSecondary">
											(Max. File size: 25 MB)
										</Mui.Typography>
									</Mui.Box>
								) : (
									<Mui.Box className={classes.attachmentsContainer}>
										{attachments.map((file, index) => (
											<Mui.Box
												key={index}
												className={classes.attachmentThumbnail}
											>
												<img
													src={URL.createObjectURL(file)}
													alt={file.name}
													className={classes.attachmentImage}
												/>
												<Mui.IconButton
													size="small"
													disabled={submitting}
													onClick={() => !submitting && this.removeAttachment(index)}
													className={classes.removeButton}
												>
													<Icons.X size={14}/>
												</Mui.IconButton>
											</Mui.Box>
										))}

										<Mui.Box
											onDragEnter={this.handleDrag}
											onDragOver={this.handleDrag}
											onDragLeave={this.handleDrag}
											onDrop={this.handleDrop}
											onClick={() => !submitting && this.fileInputRef.current?.click()}
											className={clsx(
												classes.addMoreBox,
												dragActive && classes.addMoreBoxActive,
												submitting && classes.addMoreBoxDisabled
											)}
										>
											<Mui.Box className={classes.plusIcon}>
												<Icons.Plus size={24}/>
											</Mui.Box>
										</Mui.Box>
									</Mui.Box>
								)}
							</Mui.Box>
						</Mui.Box>

						<Mui.Box className={classes.formFooter}>
							<Mui.Button
								variant="contained"
								color={"primary"}
								onClick={this.handleSubmit}
								disabled={submitting}
								className={classes.submitButton}
							>
								{submitting ? "Submitting..." : "Submit"}
							</Mui.Button>
						</Mui.Box>
					</>
				)}
				</Mui.Box>

				<Mui.Box
					className={clsx(
						classes.speechBubbleArrow,
						showSuccess ? classes.speechBubbleArrowGray : classes.speechBubbleArrowWhite
					)}
				/>
			</>
		);
	}
}

export const UserVoiceIdeaDialog = withStyles(Component, styles);
